/**
 * feat-303-curriculum-drip-release — the tenant drip block in TenantDashboard's
 * tenant header (design/NOTES.md §1, mockup tenant-drip-settings.html).
 *
 * Presentation + the rate draft only. The parent owns the writes (a targeted
 * PUT per control, then a re-fetch, like handleToggleTenantLive) and the
 * turn-off confirm. The parent also owns the ADMIN gate — this component
 * renders unconditionally, so mount it only behind the fail-closed check.
 */
import React, { useState } from 'react';
import {
  DRIP_HELP_BODY,
  DRIP_HELP_LEAD,
  DRIP_LABEL,
  DRIP_RATE_INVALID,
  DRIP_RATE_SUFFIX,
  DRIP_SAVING,
} from './dripCopy';
import { DRIP_RATE_MAX, DRIP_RATE_MIN, parseDripRate } from './dripSettings';
import InfoPopover from '../management/InfoPopover';

export interface TenantDripControlProps {
  tenantId: string;
  enabled: boolean;
  rate: number;
  pending: boolean;
  /** Already-formatted T7 string, or null. */
  error: string | null;
  onToggle: (next: boolean) => void;
  onRateSave: (rate: number) => void;
}

export default function TenantDripControl({
  tenantId,
  enabled,
  rate,
  pending,
  error,
  onToggle,
  onRateSave,
}: TenantDripControlProps) {
  const [draft, setDraft] = useState(String(rate));
  const [invalid, setInvalid] = useState(false);

  // Re-sync the draft to the PERSISTED value whenever it changes or a save
  // settles — success shows the re-fetched value, failure shows the old one.
  // Done during render on an actual CHANGE of (rate, pending), not in an effect:
  // a mount effect can be flushed late, after the admin's first keystroke, and
  // would silently overwrite it.
  const [synced, setSynced] = useState({ rate, pending });
  if (synced.rate !== rate || synced.pending !== pending) {
    setSynced({ rate, pending });
    if (!pending) {
      setDraft(String(rate));
      setInvalid(false);
    }
  }

  const commit = () => {
    const parsed = parseDripRate(draft);
    if (parsed === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (parsed === rate) {
      setDraft(String(rate)); // normalise e.g. "02"
      return;
    }
    onRateSave(parsed);
  };

  const helpId = `drip-help-${tenantId}`;
  const errId = `drip-rate-error-${tenantId}`;

  // feat-307 (D7): the help paragraph moved behind an ⓘ (InfoPopover keeps it
  // mounted while closed, so the rate input's aria-describedby still resolves).
  // The toggle and the rate stay on screen. Restyled to develop's tokens (D8).
  return (
    <div data-testid={`drip-block-${tenantId}`}>
      <div className="flex items-center gap-x-3 gap-y-2 flex-wrap">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <span className="relative inline-flex items-center">
            <input
              type="checkbox"
              data-testid={`drip-enabled-checkbox-${tenantId}`}
              checked={enabled}
              disabled={pending}
              onChange={(e) => onToggle(e.target.checked)}
              className="sr-only peer"
            />
            <span aria-hidden="true" className="w-9 h-5 rounded-full bg-cream-dark border border-softBorder peer-checked:bg-coral peer-checked:border-coral peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-coral/40 transition-colors" />
            <span aria-hidden="true" className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
          </span>
          <span className="text-sm font-semibold text-ink">{DRIP_LABEL}</span>
        </label>
        <label className="inline-flex items-center gap-1.5 text-sm text-ink">
          <input
            type="number"
            min={DRIP_RATE_MIN}
            max={DRIP_RATE_MAX}
            step={1}
            inputMode="numeric"
            data-testid={`drip-rate-input-${tenantId}`}
            value={draft}
            disabled={pending}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? `${errId} ${helpId}` : helpId}
            onChange={(e) => {
              setDraft(e.target.value);
              if (invalid) setInvalid(false);
            }}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setDraft(String(rate));
                setInvalid(false);
              }
            }}
            className={`w-14 px-2 py-0.5 border ${
              invalid ? 'border-red-500' : 'border-softBorder'
            } rounded-md text-sm bg-white disabled:opacity-50`}
          />
          {DRIP_RATE_SUFFIX}
        </label>
        <InfoPopover label={`What does ${DRIP_LABEL} do?`} testId={`drip-help-${tenantId}`} align="right">
          <p id={helpId}>
            <span className="font-semibold">{DRIP_HELP_LEAD}</span> {DRIP_HELP_BODY}
          </p>
        </InfoPopover>
        {pending && <span className="text-xs text-inkMuted">{DRIP_SAVING}</span>}
      </div>
      {invalid && (
        <p
          id={errId}
          role="alert"
          data-testid={`drip-rate-error-${tenantId}`}
          className="text-xs text-danger-text mt-1"
        >
          {DRIP_RATE_INVALID}
        </p>
      )}
      {error && !pending && (
        <p role="alert" data-testid={`drip-error-${tenantId}`} className="text-xs text-danger-text mt-1">
          {error}
        </p>
      )}
    </div>
  );
}
